// Keyboard + touch stick + mouse. Movement is read as a vector in screen space (x right, y down).
// Mouse: the hero follows the pointer over the play area (Game aims it on the ground); right-click blinks.
// A movement key hands control to the keyboard until the mouse moves again, so a parked cursor never drags you.
// floating: the stick appears under the first touch. fixed: a stick in the bottom corner, steered by any touch on its half.
export type JoystickMode = 'floating' | 'fixed';

const RADIUS = 48;
const MOVE_KEYS = new Set(['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);
// Survivors-likes are played at full speed: a small thumb movement should already run, never walk slowly.
const DEAD_ZONE = 0.12; // ~6 px of drift is ignored
const MIN_SPEED = 0.7; // speed just outside the dead zone
const FULL_AT = 0.4; // ~19 px of deflection = full speed

export class InputController {
  private readonly keys = new Set<string>();
  private stickId: number | null = null;
  private stickX = 0;
  private stickY = 0;
  private originX = 0;
  private originY = 0;
  private mode: JoystickMode = 'floating';
  private dashQueued = false;
  private mouseFollow = false;
  private mouseX = 0;
  private mouseY = 0;
  enabled = true;

  private readonly onKeyDown = (event: KeyboardEvent) => {
    if (event.repeat) return;
    if ((event.target as HTMLElement | null)?.closest?.('input, textarea, select')) return; // typing in a form
    this.keys.add(event.code);
    if (MOVE_KEYS.has(event.code)) this.mouseFollow = false;
    if (event.code === 'Space' || event.code === 'ShiftLeft' || event.code === 'ShiftRight') {
      this.dashQueued = true;
      if (event.code === 'Space') event.preventDefault();
    }
  };

  private readonly onKeyUp = (event: KeyboardEvent) => {
    this.keys.delete(event.code);
  };

  private readonly onBlur = () => {
    this.keys.clear();
    this.release();
    this.mouseFollow = false;
  };

  private readonly onVisibility = () => {
    if (document.hidden) this.onBlur();
  };

  private readonly onMouseLeave = () => {
    this.mouseFollow = false;
  };

  // Any touch marks the device as touch, so the touch-only UI (BLINK button, hints) shows even before the first run.
  private readonly onPointerSeen = (event: PointerEvent) => {
    if (event.pointerType === 'touch') document.body.classList.add('touch');
  };

  private readonly onDown = (event: PointerEvent) => {
    if (!this.enabled) return;
    if (event.pointerType === 'mouse') {
      this.mouseX = event.clientX;
      this.mouseY = event.clientY;
      if (event.button === 2) this.dashQueued = true;
      this.mouseFollow = true;
      return;
    }
    if (this.stickId !== null) return;
    if (this.mode === 'fixed') {
      const rect = this.stick.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const mid = window.innerWidth / 2;
      if ((event.clientX < mid) !== (cx < mid)) return;
      this.originX = cx;
      this.originY = cy;
    } else {
      this.originX = event.clientX;
      this.originY = event.clientY;
      this.stick.style.left = `${event.clientX}px`;
      this.stick.style.top = `${event.clientY}px`;
    }
    this.stickId = event.pointerId;
    this.stickX = 0;
    this.stickY = 0;
    this.stick.classList.add('active');
    this.updateKnob(0, 0);
  };

  private readonly onMove = (event: PointerEvent) => {
    if (event.pointerType === 'mouse') {
      this.mouseX = event.clientX;
      this.mouseY = event.clientY;
      // Follow only over the play area: hovering a HUD button (pause) does not walk you toward it.
      this.mouseFollow = event.target === this.zone;
      return;
    }
    if (event.pointerId !== this.stickId) return;
    const dx = (event.clientX - this.originX) / RADIUS;
    const dy = (event.clientY - this.originY) / RADIUS;
    const len = Math.hypot(dx, dy);
    const mag = len <= DEAD_ZONE ? 0 : Math.min(1, MIN_SPEED + ((1 - MIN_SPEED) * (len - DEAD_ZONE)) / (FULL_AT - DEAD_ZONE));
    this.stickX = len > 0 ? (dx / len) * mag : 0;
    this.stickY = len > 0 ? (dy / len) * mag : 0;
    // The knob follows the thumb (up to the rim), not the speed curve.
    const shown = Math.min(1, len) / (len || 1);
    this.updateKnob(dx * shown, dy * shown);
  };

  private readonly onUp = (event: PointerEvent) => {
    if (event.pointerType === 'mouse') return;
    if (event.pointerId === this.stickId) this.release();
  };

  private readonly onDash = (event: PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    this.dashQueued = true;
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.dashButton.animate([{ transform: 'scale(0.86)' }, { transform: 'scale(1)' }], { duration: 160, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' });
    }
  };

  constructor(
    private readonly zone: HTMLElement,
    private readonly stick: HTMLElement,
    private readonly knob: HTMLElement,
    private readonly dashButton: HTMLElement,
  ) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    document.documentElement.addEventListener('mouseleave', this.onMouseLeave);
    window.addEventListener('pointerdown', this.onPointerSeen);
    zone.addEventListener('pointerdown', this.onDown);
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
    window.addEventListener('lostpointercapture', this.onUp);
    document.addEventListener('visibilitychange', this.onVisibility);
    dashButton.addEventListener('pointerdown', this.onDash);
    if (matchMedia('(pointer: coarse)').matches) document.body.classList.add('touch');
  }

  // Left-handed only moves things: CSS places the BLINK button and the fixed stick by body.left-handed.
  configure(options: { mode: JoystickMode; leftHanded: boolean }): void {
    this.mode = options.mode;
    document.body.classList.toggle('left-handed', options.leftHanded);
    this.stick.classList.toggle('fixed', options.mode === 'fixed');
    this.stick.style.left = '';
    this.stick.style.top = '';
    this.release();
  }

  readMovement(out: { x: number; y: number }): { x: number; y: number } {
    let x = this.stickX;
    let y = this.stickY;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) x -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) x += 1;
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) y -= 1;
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) y += 1;
    const len = Math.hypot(x, y);
    if (len > 1) {
      x /= len;
      y /= len;
    }
    out.x = x;
    out.y = y;
    return out;
  }

  // Pointer position while the mouse is steering, else null.
  mouseTarget(): { x: number; y: number } | null {
    return this.mouseFollow && this.enabled ? { x: this.mouseX, y: this.mouseY } : null;
  }

  consumeDash(): boolean {
    const queued = this.dashQueued;
    this.dashQueued = false;
    return queued;
  }

  clear(): void {
    this.keys.clear();
    this.dashQueued = false;
    this.mouseFollow = false;
    this.release();
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    document.documentElement.removeEventListener('mouseleave', this.onMouseLeave);
    window.removeEventListener('pointerdown', this.onPointerSeen);
    this.zone.removeEventListener('pointerdown', this.onDown);
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onUp);
    window.removeEventListener('lostpointercapture', this.onUp);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.dashButton.removeEventListener('pointerdown', this.onDash);
  }

  private release(): void {
    this.stickId = null;
    this.stickX = 0;
    this.stickY = 0;
    this.stick.classList.remove('active');
    this.updateKnob(0, 0);
  }

  private updateKnob(x: number, y: number): void {
    this.knob.style.transform = `translate(calc(-50% + ${x * 40}px), calc(-50% + ${y * 40}px))`;
  }
}
